import React, { useState, useCallback } from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Grid,
  Box,
  ButtonBase,
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';

import useMediaQuery from '@mui/material/useMediaQuery';

import SmallMuiMarkdown from './SmallMuiMarkdown';
import ShowMoreContent from './ShowMoreContent';

/**
 * Container component for long descriptions that need expand/collapse.
 * Renders ButtonBase with separate hover effect and click handling.
 * Receives additional props from ShowMoreContent's outerContainerComponent pattern
 * that are intentionally unused here (needsTruncation, hasMeasured, expanded).
 */
const LongDescriptionContainer = ({ onToggle, children }) => {
  const baseSx = {
    display: 'block',
    width: '100%',
    textAlign: 'inherit',
    paddingLeft: { xs: 2, md: 4 },
    paddingRight: { xs: 1, md: 2 },
    paddingBottom: { xs: 2, md: 4 },
    paddingTop: { xs: 1, md: 1 },
  };

  return (
    <ButtonBase
      aria-label="Expand or collapse description"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      sx={{
        ...baseSx,
        '&:hover': {
          backgroundColor: 'action.hover',
        },
        // Disable pointer events on children to ensure the ButtonBase
        // captures all clicks for expand/collapse functionality.
        // Note: This prevents any interactive elements within markdown content.
        '& *': {
          pointerEvents: 'none',
        },
      }}
    >
      {children}
    </ButtonBase>
  );
};

export function ChooseProductCardSkeleton() {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        textAlign: 'center',
        borderLeft: '1px solid',
        borderTop: '1px solid',
        borderBottomRightRadius: '0px',
        borderTopRightRadius: '0px',
        borderBottomLeftRadius: '0px',
        borderColor: (theme) => theme.palette.divider,
        p: 0,
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          paddingTop: { xs: 2, md: 4 },
          paddingBottom: { xs: 2, md: 4 },
          paddingLeft: { xs: 2, md: 4 },
          paddingRight: { xs: 1, md: 2 },
        }}
      >
        <Box sx={{ width: '100%', p: 0, justifyContent: 'flex-start' }}>
          <Grid
            container
            spacing={1}
            sx={{
              justifyContent: 'center',
            }}
          >
            {/* Header row with cube, title, and minDuration */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                {/* Cube and Title container */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 2,
                    flex: '1 1 0',
                    minWidth: 0,
                  }}
                >
                  {/* Cube skeleton */}
                  <Box
                    sx={{
                      height: '20px',
                      width: '20px',
                      border: (theme) =>
                        `3px solid ${theme.palette.primary.main}`,
                      borderRadius: `6px`,
                      display: 'inline-block',
                      flexShrink: 0, // Cube never shrinks
                    }}
                  />
                  {/* Title skeleton */}
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={28}
                    sx={{ minWidth: 0 }}
                  />
                </Box>

                {/* MinDuration skeleton - desktop only */}
                {!isSmallScreen && (
                  <Box
                    sx={{
                      flexShrink: 0,
                      alignSelf: 'flex-start',
                    }}
                  >
                    <Skeleton variant="text" width={60} height={24} />
                  </Box>
                )}
              </Box>
            </Grid>

            {/* MinDuration skeleton - mobile only */}
            {isSmallScreen && (
              <Grid item xs={12}>
                <Skeleton variant="text" width={60} height={24} />
              </Grid>
            )}

            {/* Price skeleton */}
            <Grid item xs={12}>
              <Skeleton variant="text" width={80} height={24} />
            </Grid>

            {/* Description skeleton */}
            <Grid item xs={12}>
              <Box sx={{ mt: 0.5 }}>
                <Skeleton variant="text" width="100%" height={20} />
                <Skeleton variant="text" width="75%" height={20} />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}

export default function ChooseProductCard({
  index,
  title,
  titleTypographyProps,
  description,
  descriptionTypographyProps,
  minDuration,
  minDurationTypographyProps,
  onSelectProduct,
  sx = {},
}) {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));

  // Track truncation state: null = measuring, true = needs truncation, false = short
  const [needsTruncation, setNeedsTruncation] = useState(null);

  const handleMeasureComplete = useCallback(({ needsTruncation }) => {
    setNeedsTruncation(needsTruncation);
  }, []);

  // Card is visible only after measurement completes (or if no description to measure)
  const isReady = !description || needsTruncation !== null;

  // Common card styles with opacity fade to hide layout shift during measurement
  const cardSx = {
    '--index': index,
    textAlign: 'center',
    borderLeft: '1px solid',
    borderTop: '1px solid',
    borderBottomRightRadius: '0px',
    borderTopRightRadius: '0px',
    borderBottomLeftRadius: '0px',
    borderColor: (theme) => theme.palette.divider,
    p: 0,
    opacity: isReady ? 1 : 0,
    animation: isReady ? 'fadeIn 0.6s ease-out both' : 'none',
    animationDelay: 'calc(var(--index) * 100ms)',
    '@keyframes fadeIn': {
      from: {
        opacity: 0,
      },
      to: {
        opacity: 1,
      },
    },
    ...sx,
  };

  // Header content (title, duration) - reused in both structures
  const headerContent = (
    <CardContent sx={{ width: '100%', p: 0, justifyContent: 'flex-start' }}>
      <Grid
        container
        spacing={1}
        sx={{
          justifyContent: 'center',
        }}
      >
        {/* Header row with cube, title, and minDuration */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            {/* Cube and Title container - never wraps */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 2,
                flex: '1 1 0',
                minWidth: 0,
                lineHeight: (theme) => theme.typography?.h6?.lineHeight,
                fontSize: (theme) => theme.typography?.h6?.fontSize,
              }}
            >
              <Box
                sx={{
                  height: '20px',
                  width: '20px',
                  border: (theme) => `3px solid ${theme.palette.primary.main}`,
                  borderRadius: `6px`,
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body1"
                {...titleTypographyProps}
                sx={{
                  textAlign: 'left',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                  ...titleTypographyProps?.sx,
                }}
              >
                {title}
              </Typography>
            </Box>

            {/* MinDuration - desktop */}
            {minDuration && !isSmallScreen && (
              <Box
                sx={{
                  flexShrink: 0,
                  alignSelf: 'flex-start',
                }}
              >
                <Typography
                  variant="body2"
                  {...minDurationTypographyProps}
                  sx={{
                    textAlign: 'center',
                    color: 'text.secondary',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    lineHeight: (theme) => theme.typography?.h6?.lineHeight,
                    ...minDurationTypographyProps?.sx,
                  }}
                >
                  {`${minDuration} min`}
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>

        {/* MinDuration - mobile */}
        {minDuration && isSmallScreen && (
          <Grid item xs={12}>
            <Typography
              variant="body2"
              {...minDurationTypographyProps}
              sx={{
                textAlign: 'left',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                lineHeight: (theme) => theme.typography?.h6?.lineHeight,
                ...minDurationTypographyProps?.sx,
              }}
            >
              {`${minDuration} min`}
            </Typography>
          </Grid>
        )}
      </Grid>
    </CardContent>
  );

  // Description content with ShowMoreContent
  // Only use LongDescriptionContainer when we definitively know truncation is needed
  const descriptionContent = description && (
    <ShowMoreContent
      maxHeight={60}
      fadeHeight={24}
      onMeasureComplete={handleMeasureComplete}
      outerContainerComponent={
        needsTruncation === true ? LongDescriptionContainer : undefined
      }
    >
      <SmallMuiMarkdown
        typographyProps={{
          textAlign: 'left',
          color: 'text.secondary',
          sx: {
            lineHeight: 1.4,
            ...descriptionTypographyProps?.sx,
          },
        }}
      >
        {description}
      </SmallMuiMarkdown>
    </ShowMoreContent>
  );

  // SHORT, NO DESCRIPTION, or MEASURING: Single unified CardActionArea
  // During measurement (needsTruncation === null), we render the unified
  // structure to avoid layout shift if the description turns out to be short.
  // Only switch to two-area structure when we definitively know truncation is needed.
  if (!description || needsTruncation !== true) {
    return (
      <Card sx={cardSx}>
        <CardActionArea
          onClick={onSelectProduct}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            paddingTop: { xs: 2, md: 4 },
            paddingBottom: description ? 0 : { xs: 2, md: 4 },
            paddingLeft: { xs: 2, md: 4 },
            paddingRight: { xs: 1, md: 2 },
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          {headerContent}
          {description && (
            <Box
              sx={{
                width: '100%',
                paddingBottom: { xs: 2, md: 4 },
              }}
            >
              {descriptionContent}
            </Box>
          )}
        </CardActionArea>
      </Card>
    );
  }

  // LONG DESCRIPTION ONLY (needsTruncation === true): Two separate action areas
  return (
    <Card sx={cardSx}>
      {/* Upper action area - selects product */}
      <CardActionArea
        onClick={onSelectProduct}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          paddingTop: { xs: 2, md: 4 },
          paddingBottom: 1,
          paddingLeft: { xs: 2, md: 4 },
          paddingRight: { xs: 1, md: 2 },
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        {headerContent}
      </CardActionArea>

      {/* Lower area - expandable description with separate hover/click */}
      {descriptionContent}
    </Card>
  );
}
